/**
 * ReviewDocumentRow - one document in the review's documents pane.
 *
 * ONE anatomy for every row: `[status glyph | main | right]`, always all
 * three, always the same height. The previous row grew and shrank - a
 * progress bar only when there were findings, a line of coloured dot-counts
 * only when several severities were present - so no two rows were the same
 * shape and the list could not be scanned down. Everything conditional is now
 * expressed INSIDE a fixed slot: the glyph changes state, it does not appear
 * and disappear.
 *
 * The row SELECTS. It does not open the viewer - that is the hover icon on
 * the right, and the document link in the finding header. Scoping the
 * findings to a document is the thing you do constantly; opening the 80%
 * viewer is not, and conflating them meant dismissing a panel every time.
 */

import React from 'react';
import clsx from 'clsx';
import { Tooltip } from '@mui/material';
import { PiArrowSquareOutLight, PiCheckBold, PiFileTextLight } from 'react-icons/pi';
import { documentLabel } from './documentName';

export interface DocumentRowStat {
  total: number;
  /** RESOLVED findings only - escalated is open and excluded. */
  decided: number;
  /** Escalated findings: work done, but not an answer. */
  escalated?: number;
  /** Per-severity counts. `critical` and `major` are surfaced on the row. */
  bySeverity: Partial<Record<'critical' | 'major' | 'minor' | 'info', number>>;
  /**
   * The HIGHEST risk score among this document's findings.
   *
   * Not an average: averaging a 9 with eleven 2s produces a calm number for
   * a document with a serious problem in it, which is the opposite of what
   * a list is for. The worst thing in the document is the thing that decides
   * whether you open it next.
   */
  maxRisk?: number | null;
}

interface Props {
  fileName: string | null;
  stat?: DocumentRowStat;
  /** Still processing - no counts to show yet. */
  isProcessing?: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onOpen: () => void;
  /** Staggered reveal, as the research history rows do. */
  index?: number;
  /** Already-translated strings - this component holds no copy of its own. */
  labels: {
    notStarted: string;
    inReview: string;
    complete: string;
    findings: string;
    critical: string;
    major: string;
    risk: string;
    /** Tooltip on the risk figure - it is the worst one, not an average. */
    topRisk: string;
    open: string;
  };
}

type RowState = 'not-started' | 'in-review' | 'complete';

/**
 * The status glyph.
 *
 * A ring rather than a bar, because it belongs in a fixed-size slot on the
 * left edge where a bar cannot go - and because the same 26px circle can
 * carry all three states without changing the row's shape.
 */
const StatusGlyph: React.FC<{
  state: RowState;
  /** 0–1. Only read in `in-review`. */
  progress: number;
  number: string | null;
  isSelected: boolean;
}> = ({ state, progress, number, isSelected }) => {
  const inner = number ? (
    <span className="text-[10px] font-medium tabular-nums text-fg-body leading-none">
      {number}
    </span>
  ) : (
    // No numeric prefix in the filename - a glyph rather than an invented
    // number or an empty circle.
    <PiFileTextLight className="w-3 h-3 text-fg-muted" aria-hidden />
  );

  if (state === 'complete') {
    return (
      <span
        data-testid="doc-glyph"
        data-state="complete"
        className="w-[26px] h-[26px] rounded-full bg-success-500 flex items-center justify-center flex-shrink-0"
      >
        <PiCheckBold className="w-3 h-3 text-white" aria-hidden />
      </span>
    );
  }

  if (state === 'not-started') {
    return (
      <span
        data-testid="doc-glyph"
        data-state="not-started"
        className="w-[26px] h-[26px] rounded-full border border-dashed border-hairline
                   flex items-center justify-center flex-shrink-0"
      >
        {inner}
      </span>
    );
  }

  /*
    In review: a champagne arc over a faint champagne track, with the middle
    punched out. Conic-gradient has no Tailwind utility, so it is inline —
    but it reads the champagne TOKEN, never a hex value.

    The punched-out middle has to be opaque or the arc shows through it, and
    it has to match what the row sits on. `--surface` is that background; when
    the row is selected the champagne wash is composited over it here too, so
    the hole matches the selected row rather than punching a pale disc in it.
  */
  const pct = Math.round(Math.max(0, Math.min(1, progress)) * 100);
  return (
    <span
      data-testid="doc-glyph"
      data-state="in-review"
      data-progress={pct}
      className="relative w-[26px] h-[26px] rounded-full flex-shrink-0"
      style={{
        background: `conic-gradient(rgb(var(--champagne)) ${pct}%, rgb(var(--champagne) / 0.18) ${pct}%)`,
      }}
    >
      <span
        className="absolute inset-[3px] rounded-full flex items-center justify-center"
        style={{
          background: isSelected
            ? 'linear-gradient(rgb(var(--champagne) / 0.12), rgb(var(--champagne) / 0.12)), rgb(var(--surface))'
            : 'rgb(var(--surface))',
        }}
      >
        {inner}
      </span>
    </span>
  );
};

const ReviewDocumentRow: React.FC<Props> = ({
  fileName,
  stat,
  isProcessing,
  isSelected,
  onSelect,
  onOpen,
  index = 0,
  labels,
}) => {
  const { number, title } = documentLabel(fileName);
  const total = stat?.total ?? 0;
  const decided = stat?.decided ?? 0;
  const critical = stat?.bySeverity?.critical ?? 0;
  const major = stat?.bySeverity?.major ?? 0;
  const maxRisk = stat?.maxRisk ?? null;

  /*
    `decided` excludes escalated (see `isResolvedDecision`), so a document
    with an escalated finding can never reach 'complete' - which is the
    point. But escalating IS work, so a document whose only activity is an
    escalation reads as in-review rather than untouched.
  */
  const escalated = stat?.escalated ?? 0;
  const state: RowState =
    total > 0 && decided >= total
      ? 'complete'
      : decided > 0 || escalated > 0
      ? 'in-review'
      : 'not-started';

  const statusLabel =
    state === 'complete' ? labels.complete
      : state === 'in-review' ? labels.inReview
      : labels.notStarted;

  return (
    <div
      data-testid="review-document-row"
      data-active={isSelected}
      data-state={state}
      style={{ '--reveal-delay': `${Math.min(index, 8) * 35}ms` } as React.CSSProperties}
      /* The ORIGINAL filename, kept reachable - the title is a rendering of
         it, not a replacement for it. */
      title={fileName || undefined}
      className={clsx(
        'group relative reveal-item w-full rounded-xl mb-0.5 overflow-hidden',
        'transition-all duration-200 ease-slide',
        isSelected
          ? 'bg-champagne/10 ring-1 ring-inset ring-champagne/50'
          : 'hover:bg-surface/80 hover:shadow-md hover:ring-1 hover:ring-hairline-soft'
      )}
    >
      {/* Flush to the row's edge, solid, and thick enough to read as a marker
          rather than a border. The old hairline rail was the main reason a
          selected document did not look selected. */}
      <span
        aria-hidden
        className={clsx(
          'absolute start-0 top-0 bottom-0 w-[3px] bg-champagne',
          'transition-opacity duration-200 ease-slide',
          isSelected ? 'opacity-100' : 'opacity-0'
        )}
      />

      <button
        type="button"
        data-testid="review-document-select"
        onClick={onSelect}
        className="w-full text-start px-3 py-2.5"
      >
        <div className="grid grid-cols-[26px_1fr_auto] items-center gap-3">
          <StatusGlyph
            state={state}
            progress={total > 0 ? decided / total : 0}
            number={number}
            isSelected={isSelected}
          />

          <div className="min-w-0">
            <p className={clsx(
              'text-[13px] truncate leading-snug transition-colors duration-200 ease-slide',
              isSelected ? 'font-medium text-fg-strong' : 'font-medium text-fg-body'
            )}>
              {title}
            </p>
            {/*
              What is IN this document, now that the pane is wide enough to
              say it: the worst risk score, then the counts that decide
              whether you open it next. It used to be critical-or-nothing
              plus a total, on a 280px column that had no room for more.
              Every part is still optional and every part keeps its slot, so
              rows stay the same height whatever they carry.
            */}
            <p className="text-[11px] text-fg-muted truncate mt-0.5">
              {isProcessing ? (
                labels.notStarted
              ) : total === 0 ? (
                <span>0 {labels.findings}</span>
              ) : (
                <>
                  {typeof maxRisk === 'number' && (
                    <>
                      <span
                        data-testid="doc-max-risk"
                        title={labels.topRisk}
                        className={clsx(
                          'font-medium tabular-nums',
                          maxRisk >= 7 ? 'text-danger-600 dark:text-danger-400'
                            : maxRisk >= 4 ? 'text-warning-600 dark:text-warning-400'
                            : 'text-success-600 dark:text-success-400',
                        )}
                      >
                        {labels.risk} {maxRisk}
                      </span>
                      <span className="mx-1 text-fg-muted/60" aria-hidden>·</span>
                    </>
                  )}
                  {critical > 0 && (
                    <>
                      <span className="font-medium text-danger-600 dark:text-danger-400 tabular-nums">
                        {critical} {labels.critical}
                      </span>
                      <span className="mx-1 text-fg-muted/60" aria-hidden>·</span>
                    </>
                  )}
                  {major > 0 && (
                    <>
                      <span className="font-medium text-warning-700 dark:text-warning-400 tabular-nums">
                        {major} {labels.major}
                      </span>
                      <span className="mx-1 text-fg-muted/60" aria-hidden>·</span>
                    </>
                  )}
                  <span className="tabular-nums">{total} {labels.findings}</span>
                </>
              )}
            </p>
          </div>

          {/* Slides left on hover so the open-icon never lands on top of the
              count. Cheaper than fading the count out, which would hide the
              number exactly while you are reaching for the row. */}
          <div className="text-end flex-shrink-0 transition-[margin] duration-200 ease-slide
                          group-hover:me-6">
            <p className="text-[12px] tabular-nums leading-none">
              <span className="font-medium text-fg-strong">{decided}</span>
              <span className="text-fg-muted">/{total}</span>
            </p>
            <p className={clsx(
              'mt-1 text-[11px] font-medium leading-none',
              state === 'complete' ? 'text-success-600 dark:text-success-400'
                : state === 'in-review' ? 'text-champagne'
                : 'text-fg-muted'
            )}>
              {statusLabel}
            </p>
          </div>
        </div>
      </button>

      {/* The ONLY thing in this list that opens the viewer. */}
      <Tooltip title={labels.open} placement="left">
        <button
          type="button"
          data-testid="review-document-open"
          aria-label={labels.open}
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="absolute end-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-fg-muted
                     opacity-0 group-hover:opacity-100 focus-visible:opacity-100
                     hover:text-champagne hover:bg-surface-sunken
                     transition-all duration-200 ease-slide"
        >
          <PiArrowSquareOutLight className="w-3.5 h-3.5" />
        </button>
      </Tooltip>
    </div>
  );
};

export default ReviewDocumentRow;
